import { useState, useEffect, useLayoutEffect, useMemo, useRef } from "react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, Link, useLocation } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { useAuthStore } from "@/store/authStore";
import { useNavigationGuardStore } from "@/store/navigationGuardStore";
import { LanguageSwitcher } from "@/i18n/components/LanguageSwitcher";
import {
  AlertModal,
  Alert,
  Button,
  Modal,
  ModalFooter,
  brandFillClass,
} from "@/components/ui";
import { AppBrandLogo } from "@/components/brand/AppBrandLogo";
import { TruncatedText } from "@/components/ui";
import { SidebarPrivacyMenu } from "@/components/legal/SidebarPrivacyMenu";
import {
  QuickSearchDialog,
  quickSearchShortcutLabel,
  type QuickSearchDestination,
} from "@/components/common/QuickSearchDialog";
import { AppThemeToggle } from "@/components/common/AppThemeToggle";
import {
  ENVIRONMENT_BANNER_TOP_OFFSET_CLASS,
} from "@/components/environmentBannerLayout";
import {
  filterNavItems,
  findActiveTrail,
  isNavPathActive,
  type NavGroupId,
  type NavItem,
  type NavItemDef,
} from "@/components/layoutNavigation";
import { useEnvironmentBannerState } from "@/hooks/useEnvironmentBannerState";
import { useImpersonation } from "@/hooks/useImpersonation";
import { usePolledAdminNotificationStatusCounts } from "@/hooks/useAdminNotificationStatusCounts";
import { motion } from "motion/react";
import { cn } from "@/lib/utils";
import { legalInfoQueryKey, profileService } from "@/services/profileService";
import { activityService } from "@/services/activityService";
import { bookingKeys, bookingService } from "@/services/bookingService";
import { Permission, UserRole, type BookingListFilters } from "@/types";
import {
  LayoutDashboard,
  Store,
  Calendar,
  Users,
  Wallet,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronRight,
  HelpCircle,
  Ticket,
  Tablet,
  TabletSmartphone,
  CreditCard,
  Home,
  Award,
  Landmark,
  Bell,
  FileText,
  MessageSquareText,
  MessageCircle,
  MessageCircleMore,
  Undo2,
  UserCog,
  Mail,
  Images,
  ChartColumn,
  Layers,
  HandCoins,
  Settings2,
  Search,
} from "lucide-react";

interface LayoutProps {
  children: ReactNode;
}

/**
 * `--content-sticky-top`: height covered by the sticky topbar (h-14), plus the
 * environment banner (h-8) when shown. Sticky page content offsets from it.
 */
/** Partner scope is implicit (own activities); same key as the dashboard to-do panel. */
const PARTNER_ATTENTION_FILTERS: BookingListFilters = {};

const CONTENT_STICKY_TOP_CLASS = "[--content-sticky-top:3.5rem]";
const CONTENT_STICKY_TOP_WITH_BANNER_CLASS = "[--content-sticky-top:5.5rem]";

const NAV_GROUP_ORDER: readonly NavGroupId[] = [
  "overview",
  "management",
  "operations",
  "finance",
  "account",
];

function getNavItemDefinitions(): NavItemDef[] {
  return [
    {
      id: "dashboard",
      group: "overview",
      labelKey: "dashboard",
      path: "/",
      icon: <LayoutDashboard className="h-4 w-4" />,
    },
    {
      id: "users",
      group: "management",
      labelKey: "users",
      path: "/admin/users",
      icon: <Users className="h-4 w-4" />,
      permission: Permission.USERS_READ_ANY,
    },
    {
      id: "admin-activities",
      group: "management",
      labelKey: "adminActivities",
      path: "/admin/activities",
      icon: <Store className="h-4 w-4" />,
      permission: Permission.ACTIVITIES_MANAGE_ANY,
    },
    {
      id: "admin-structures",
      group: "management",
      labelKey: "adminStructures",
      path: "/admin/structures",
      icon: <Home className="h-4 w-4" />,
      permission: Permission.HOUSES_READ_ANY,
    },
    {
      id: "activities",
      group: "management",
      labelKey: "activities",
      path: "/activities",
      icon: <Store className="h-4 w-4" />,
      permission: Permission.ACTIVITIES_UPDATE,
      hideForRoles: [UserRole.ADMIN],
    },
    {
      id: "houses",
      group: "management",
      labelKey: "myHouses",
      path: "/houses",
      icon: <Home className="h-4 w-4" />,
      permission: Permission.HOUSES_READ,
      hideForRoles: [UserRole.ADMIN],
    },
    {
      id: "bookings",
      group: "management",
      labelKey: "bookings",
      path: "/bookings",
      icon: <Calendar className="h-4 w-4" />,
      permission: Permission.BOOKINGS_READ,
      hideForRoles: [UserRole.ADMIN],
    },
    {
      id: "associations",
      group: "management",
      labelKey: "partnersAndHosts",
      path: "/associations",
      icon: <Users className="h-4 w-4" />,
      permission: Permission.SALES_AGENT_ASSIGNMENTS_READ,
      hideForRoles: [UserRole.ADMIN],
    },
    {
      id: "wallet",
      group: "finance",
      labelKey: "wallet",
      path: "/wallet",
      icon: <Wallet className="h-4 w-4" />,
      permission: Permission.WALLET_READ,
      hideForRoles: [UserRole.ADMIN],
    },
    {
      id: "points",
      group: "finance",
      labelKey: "points",
      path: "/points",
      icon: <Award className="h-4 w-4" />,
      permission: Permission.CUSTOMER_POINTS_READ,
      hideForRoles: [UserRole.ADMIN],
    },
    {
      id: "settings",
      group: "account",
      labelKey: "settings",
      path: "/settings",
      icon: <Settings className="h-4 w-4" />,
      permission: Permission.USERS_UPDATE_SELF,
      hideForRoles: [UserRole.CUSTOMER, UserRole.ADMIN],
      settingsProfileBadge: true,
    },
    {
      id: "help",
      group: "account",
      labelKey: "helpSupport",
      path: "/support",
      icon: <HelpCircle className="h-4 w-4" />,
      permission: Permission.SUPPORT_TICKETS_CREATE,
      hideForRoles: [UserRole.ADMIN],
    },
    {
      id: "devices",
      group: "operations",
      labelKey: "devices",
      path: "/devices",
      icon: <TabletSmartphone className="h-4 w-4" />,
      children: [
        {
          id: "devices-list",
          labelKey: "devicesList",
          path: "/devices",
          icon: <Tablet className="h-4 w-4" />,
          permission: Permission.DEVICES_READ,
          exactPath: true,
        },
        {
          id: "devices-presets",
          labelKey: "devicesPresets",
          path: "/devices?tab=presets",
          icon: <Settings2 className="h-4 w-4" />,
          permission: Permission.DEVICES_READ,
          showInSidebar: false,
        },
        {
          id: "devices-readers",
          labelKey: "readers",
          path: "/readers",
          icon: <CreditCard className="h-4 w-4" />,
          permission: Permission.READERS_READ,
        },
        {
          id: "devices-carousel",
          labelKey: "devicesCarousel",
          path: "/devices/carousel",
          icon: <Images className="h-4 w-4" />,
          permission: Permission.CAROUSEL_SLIDES_READ,
        },
        {
          id: "devices-statistics",
          labelKey: "devicesStatistics",
          path: "/devices/statistics",
          icon: <ChartColumn className="h-4 w-4" />,
          permission: Permission.STATS_READ,
        },
        {
          id: "devices-voice-transcripts",
          labelKey: "adminAgentTranscripts",
          path: "/devices/voice-transcripts",
          icon: <MessageSquareText className="h-4 w-4" />,
          permission: Permission.AGENT_VOICE_TRANSCRIPTS_READ,
        },
      ],
    },
    {
      id: "admin-tickets",
      group: "operations",
      labelKey: "supportTickets",
      path: "/admin/tickets",
      icon: <MessageCircleMore className="h-4 w-4" />,
      children: [
        {
          id: "admin-tickets-list",
          labelKey: "supportTicketsList",
          path: "/admin/tickets",
          icon: <Ticket className="h-4 w-4" />,
          permission: Permission.SUPPORT_TICKETS_READ_ANY,
        },
        {
          id: "admin-chats",
          labelKey: "supportChats",
          path: "/admin/chats",
          icon: <MessageCircle className="h-4 w-4" />,
          permission: Permission.CUSTOMER_SUPPORT_CHATS_READ_ANY,
        },
      ],
    },
    {
      id: "admin-payments",
      group: "finance",
      labelKey: "adminPayments",
      path: "/admin/payments",
      icon: <Landmark className="h-4 w-4" />,
      children: [
        {
          id: "admin-payments-events",
          labelKey: "adminPaymentsEvents",
          path: "/admin/payments/events",
          icon: <Layers className="h-4 w-4" />,
          permission: Permission.ECONOMIC_EVENTS_LIST,
        },
        {
          id: "admin-payments-external",
          labelKey: "adminPaymentsExternal",
          path: "/admin/payments/external-payments",
          icon: <CreditCard className="h-4 w-4" />,
          permission: Permission.EXTERNAL_PAYMENTS_LIST,
        },
        {
          id: "admin-payments-settlements",
          labelKey: "adminPaymentsSettlements",
          path: "/admin/payments/settlements",
          icon: <HandCoins className="h-4 w-4" />,
          permission: Permission.SETTLEMENTS_LIST,
        },
      ],
    },
    {
      id: "admin-notifications",
      group: "operations",
      labelKey: "adminNotifications",
      path: "/admin/notifications",
      icon: <Bell className="h-4 w-4" />,
      permission: Permission.NOTIFICATIONS_READ,
    },
    {
      id: "admin-protocols",
      group: "operations",
      labelKey: "adminProtocols",
      path: "/admin/protocols",
      icon: <FileText className="h-4 w-4" />,
      permission: Permission.PROTOCOLS_READ,
    },
  ];
}

const NAV_LINK_CLASS =
  "group relative isolate flex h-8 items-center gap-2.5 rounded-lg px-2.5 text-[13px] font-medium transition-colors duration-150";
const NAV_LINK_ACTIVE_CLASS = "text-surface-900";
const NAV_LINK_IDLE_CLASS =
  "text-surface-600 hover:bg-surface-0/60 hover:text-surface-900 dark:hover:bg-surface-100/60";
/** Shared-layout pill that glides between active nav entries. */
const NAV_ACTIVE_PILL_CLASS =
  "absolute inset-0 -z-10 rounded-lg bg-surface-0 shadow-xs ring-1 ring-surface-200 dark:bg-surface-100 dark:ring-surface-200";
const NAV_PILL_TRANSITION = { type: "spring", stiffness: 520, damping: 40 } as const;
const TOPBAR_ICON_BUTTON_CLASS =
  "inline-flex h-8 w-8 items-center justify-center rounded-lg text-surface-500 transition-colors hover:bg-surface-100 hover:text-surface-900";

const ROLE_LABEL_KEYS: Partial<Record<UserRole, string>> = {
  [UserRole.ADMIN]: "roles.admin",
  [UserRole.SELLER]: "roles.seller",
  [UserRole.HOST]: "roles.host",
  [UserRole.SALES_AGENT]: "roles.sales-agent",
};

function userRoleLabel(role: UserRole, t: (key: string) => string): string {
  const key = ROLE_LABEL_KEYS[role];
  return key ? t(key) : "";
}

/**
 * Replays the CSS enter animation on every path change without remounting the
 * page: a `key` would reset page state and refetch skeletons on param-only
 * navigations (e.g. booking → booking).
 */
function RouteEnterTransition({
  pathname,
  children,
}: {
  pathname: string;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) {
      return;
    }
    // A finished CSS animation is not exposed by getAnimations(); restart it by
    // clearing it for one style flush.
    element.style.animation = "none";
    void element.offsetWidth;
    element.style.animation = "";
  }, [pathname]);

  return (
    <div ref={ref} className="flex flex-1 flex-col motion-safe:animate-slide-up">
      {children}
    </div>
  );
}

export default function Layout({ children }: LayoutProps) {
  const { t: tNav } = useTranslation("nav");
  const { t: tCommon } = useTranslation("common");
  const { t: tAdminUsers } = useTranslation("adminUsers");
  const navigate = useNavigate();
  const [quickSearchOpen, setQuickSearchOpen] = useState(false);
  const location = useLocation();
  const {
    user,
    logout,
    isAdmin,
    isHost,
    isCustomer,
    hasPermission,
    getCurrentUserId,
    isImpersonating,
  } = useAuthStore();
  const { stopImpersonation, isStopping } = useImpersonation();
  const impersonating = isImpersonating();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showImpersonationLogoutModal, setShowImpersonationLogoutModal] =
    useState(false);
  const isBlocking = useNavigationGuardStore((s) => s.isBlocking);
  const unsavedPromptOpen = useNavigationGuardStore((s) => s.unsavedPromptOpen);
  const openUnsavedPrompt = useNavigationGuardStore((s) => s.openUnsavedPrompt);
  const closeUnsavedPrompt = useNavigationGuardStore(
    (s) => s.closeUnsavedPrompt,
  );
  const { visible: environmentBannerVisible } = useEnvironmentBannerState();

  const navDefinitions = useMemo((): NavItem[] => {
    const localize = (item: NavItemDef): NavItem => ({
      id: item.id,
      group: item.group,
      label: tNav(item.labelKey),
      path: item.path,
      icon: item.icon,
      permission: item.permission,
      hideForRoles: item.hideForRoles,
      settingsProfileBadge: item.settingsProfileBadge,
      exactPath: item.exactPath,
      showInSidebar: item.showInSidebar,
      children: item.children?.map(localize),
    });
    return getNavItemDefinitions().map(localize);
  }, [tNav]);

  const isHostUser = isHost();
  const hostUserId = getCurrentUserId();

  const { data: hostActivities = [], isSuccess: hostActivitiesLoaded } =
    useQuery({
      queryKey: ["activities", "host", hostUserId],
      queryFn: () => activityService.getAll(),
      enabled: isHostUser && !!hostUserId,
    });

  const hostHasNoActivities =
    isHostUser && hostActivitiesLoaded && hostActivities.length === 0;

  const showAdminNotifications = hasPermission(Permission.NOTIFICATIONS_READ);
  const { data: notificationCounts } = usePolledAdminNotificationStatusCounts(
    showAdminNotifications,
  );
  const openNotificationCount = notificationCounts?.open ?? 0;

  const isPartnerUser =
    user?.role === UserRole.SELLER || user?.role === UserRole.HOST;
  // A host may have no activities (no bookings): wait until theirs are known.
  const hostHasActivities = hostActivitiesLoaded && hostActivities.length > 0;
  const showPartnerBookingsQuery =
    isPartnerUser &&
    !!hostUserId &&
    hasPermission(Permission.BOOKINGS_READ) &&
    (!isHostUser || hostHasActivities);
  // Loaded once; booking actions invalidate ["bookings"] and refresh the badge.
  const { data: partnerAttention } = useQuery({
    queryKey: bookingKeys.attention(PARTNER_ATTENTION_FILTERS),
    queryFn: () => bookingService.getAttention(PARTNER_ATTENTION_FILTERS),
    enabled: showPartnerBookingsQuery,
  });
  const newPartnerBookingCount =
    partnerAttention?.by_action.CONFIRMATION ?? 0;

  const filteredNavItems = useMemo(() => {
    if (!user) return [];
    return filterNavItems(
      navDefinitions,
      user.role,
      hasPermission,
      hostHasNoActivities,
    );
  }, [user, hasPermission, navDefinitions, hostHasNoActivities]);

  const quickSearchDestinations = useMemo((): QuickSearchDestination[] => {
    const destinations: QuickSearchDestination[] = [];
    for (const item of filteredNavItems) {
      if (item.children && item.children.length > 0) {
        for (const child of item.children) {
          destinations.push({
            id: child.id,
            label: child.label,
            path: child.path,
            icon: child.icon,
            group: item.label,
          });
        }
        continue;
      }
      destinations.push({
        id: item.id,
        label: item.label,
        path: item.path,
        icon: item.icon,
      });
    }
    return destinations;
  }, [filteredNavItems]);

  const legalProfileUserId = user?.sub;
  const shouldLoadLegalProfile =
    !!legalProfileUserId &&
    user?.role !== UserRole.ADMIN &&
    user?.role !== UserRole.CUSTOMER;

  const { data: legalProfile } = useQuery({
    queryKey: legalInfoQueryKey(legalProfileUserId ?? ""),
    queryFn: () => profileService.getProfile(legalProfileUserId!),
    enabled: shouldLoadLegalProfile,
  });

  const profileIncomplete =
    shouldLoadLegalProfile &&
    legalProfile !== undefined &&
    !legalProfile.profile_completed;

  useEffect(() => {
    if (!isBlocking) {
      return;
    }

    const handleDocumentClickCapture = (event: MouseEvent) => {
      if (event.defaultPrevented) {
        return;
      }
      const el = event.target;
      if (!(el instanceof Element)) {
        return;
      }
      const anchor = el.closest("a[href]");
      if (!anchor) {
        return;
      }
      if (anchor.closest("[data-skip-navigation-guard]")) {
        return;
      }

      const href = anchor.getAttribute("href");
      if (!href || href.startsWith("#")) {
        return;
      }

      let url: URL;
      try {
        url = new URL(href, window.location.href);
      } catch {
        return;
      }

      if (url.origin !== window.location.origin) {
        return;
      }

      if (
        url.pathname === location.pathname &&
        url.search === location.search
      ) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      openUnsavedPrompt();
    };

    document.addEventListener("click", handleDocumentClickCapture, true);
    return () => {
      document.removeEventListener("click", handleDocumentClickCapture, true);
    };
  }, [isBlocking, location.pathname, location.search, openUnsavedPrompt]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const handleLogoutClick = () => {
    if (impersonating) {
      setShowImpersonationLogoutModal(true);
      return;
    }
    if (isBlocking) {
      openUnsavedPrompt();
      return;
    }
    handleLogout();
  };

  const handleConfirmLogoutWhileImpersonating = () => {
    setShowImpersonationLogoutModal(false);
    if (isBlocking) {
      openUnsavedPrompt();
      return;
    }
    handleLogout();
  };

  const handleStopImpersonatingFromLogoutModal = () => {
    setShowImpersonationLogoutModal(false);
    handleStopImpersonatingClick();
  };

  const handleStopImpersonatingClick = () => {
    if (isBlocking) {
      openUnsavedPrompt();
      return;
    }
    void stopImpersonation();
  };

  const closeSidebar = () => setSidebarOpen(false);

  const navGroups = NAV_GROUP_ORDER.map((groupId) => ({
    id: groupId,
    label: tNav(`groups.${groupId}`),
    items: filteredNavItems.filter((item) => (item.group ?? "overview") === groupId),
  })).filter((group) => group.items.length > 0);

  const breadcrumb = findActiveTrail(filteredNavItems, location.pathname, (group) =>
    tNav(`groups.${group}`),
  );
  const roleLabel = user ? userRoleLabel(user.role, tAdminUsers) : "";
  const userInitials = user?.email ? user.email.slice(0, 2).toUpperCase() : "";

  return (
    <div
      className={cn(
        "min-h-screen bg-surface-50 lg:bg-canvas",
        environmentBannerVisible
          ? CONTENT_STICKY_TOP_WITH_BANNER_CLASS
          : CONTENT_STICKY_TOP_CLASS,
      )}
    >
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 animate-fade-in bg-surface-950/40 backdrop-blur-[2px] lg:hidden dark:bg-black/70"
          onClick={closeSidebar}
        />
      )}

      <div className="flex min-h-screen">
        {/* Sidebar */}
        <aside
          className={cn(
            "z-50 w-64 shrink-0 border-r border-surface-200 bg-surface-50 lg:border-r-0 lg:bg-transparent",
            "fixed left-0 transform transition-transform duration-300 ease-out",
            environmentBannerVisible
              ? cn(ENVIRONMENT_BANNER_TOP_OFFSET_CLASS, "h-[calc(100dvh-2rem)]")
              : "top-0 h-dvh",
            "lg:sticky lg:self-start",
            environmentBannerVisible ? "lg:top-8" : "lg:top-0",
            "lg:translate-x-0",
            sidebarOpen ? "translate-x-0 shadow-popover" : "-translate-x-full",
          )}
        >
          <div className="flex h-full flex-col">
            {/* Logo */}
            <div className="flex h-16 shrink-0 items-center justify-between gap-2 px-5 lg:h-[72px]">
              <Link to="/" onClick={closeSidebar} className="block h-9 w-28">
                <AppBrandLogo background="light" className="h-full w-full" />
              </Link>
              <button
                type="button"
                onClick={closeSidebar}
                aria-label={tNav("closeMenu")}
                className="rounded-md p-1.5 text-surface-500 hover:bg-surface-100 hover:text-surface-900 lg:hidden"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {impersonating ? (
              <div className="mx-3 mb-2 rounded-lg border border-warning-600/25 bg-warning-50 p-3">
                <p className="flex items-center gap-1.5 text-xs font-semibold text-warning-900">
                  <UserCog className="h-3.5 w-3.5 shrink-0" aria-hidden />
                  {tNav("impersonating")}
                </p>
                {user?.email ? (
                  <p className="mt-1 flex min-w-0 items-center gap-1.5 text-xs text-warning-800">
                    <Mail className="h-3 w-3 shrink-0" aria-hidden />
                    <TruncatedText>{user.email}</TruncatedText>
                  </p>
                ) : null}
                <Button
                  type="button"
                  variant="outline"
                  size="xs"
                  onClick={handleStopImpersonatingClick}
                  isLoading={isStopping}
                  className="mt-2.5 w-full"
                  leftIcon={
                    !isStopping ? (
                      <Undo2 className="h-3.5 w-3.5 shrink-0" aria-hidden />
                    ) : undefined
                  }
                >
                  {tNav("stopImpersonating")}
                </Button>
              </div>
            ) : null}

            {/* Seller: legal profile blocks catalog visibility */}
            {profileIncomplete && user?.role === UserRole.SELLER ? (
              <Alert variant="warning" className="mx-3 mb-2 px-3 py-2.5 text-xs">
                <p className="font-semibold text-warning-900">
                  {tNav("completeProfileTitle")}
                </p>
                <p className="mt-1 leading-relaxed">
                  {tNav("completeProfileDescription")}
                </p>
                <Link
                  to="/settings"
                  onClick={closeSidebar}
                  className="mt-1.5 inline-block font-medium text-primary-600 hover:text-primary-700 hover:underline"
                >
                  {tNav("completeProfileLink")}
                </Link>
              </Alert>
            ) : null}
            {/* Other roles: lighter profile reminder */}
            {profileIncomplete &&
            !isAdmin() &&
            !isCustomer() &&
            user?.role !== UserRole.SELLER ? (
              <Alert variant="warning" className="mx-3 mb-2 px-3 py-2 text-xs">
                {tNav("completeProfile")}
              </Alert>
            ) : null}

            {/* Navigation */}
            <nav className="flex-1 space-y-5 overflow-y-auto px-3 pb-4 pt-1">
              {navGroups.map((group) => (
                <div key={group.id}>
                  <p className="mb-1 px-2.5 text-[11px] font-medium uppercase tracking-[0.08em] text-surface-400">
                    {group.label}
                  </p>
                  <div className="space-y-0.5">
                    {group.items.map((item) => {
                      const childItems = (item.children ?? []).filter(
                        (child) => child.showInSidebar !== false,
                      );
                      const sectionOpen =
                        childItems.length > 0 &&
                        ((item.children ?? []).some((child) =>
                          isNavPathActive(
                            location.pathname,
                            child.path,
                            child.exactPath,
                          ),
                        ) ||
                          // Carousel create/edit still live under /admin/carousel/*
                          (item.id === "devices" &&
                            location.pathname.startsWith("/admin/carousel")));
                      const isActive =
                        childItems.length === 0 &&
                        isNavPathActive(
                          location.pathname,
                          item.path,
                          item.exactPath,
                        );
                      // Partner + sales agent: show profile incomplete badge on Settings (not host).
                      const showSettingsBadge =
                        item.settingsProfileBadge &&
                        profileIncomplete &&
                        (user?.role === UserRole.SELLER ||
                          user?.role === UserRole.SALES_AGENT);
                      let countBadge: { count: number; label: string } | null =
                        null;
                      if (
                        item.id === "admin-notifications" &&
                        openNotificationCount > 0
                      ) {
                        countBadge = {
                          count: openNotificationCount,
                          label: tNav("adminNotificationsOpen", {
                            count: openNotificationCount,
                          }),
                        };
                      } else if (
                        item.id === "bookings" &&
                        newPartnerBookingCount > 0
                      ) {
                        countBadge = {
                          count: newPartnerBookingCount,
                          label: tNav("bookingsNew", {
                            count: newPartnerBookingCount,
                          }),
                        };
                      }
                      return (
                        <div key={item.id}>
                          <Link
                            to={item.path}
                            onClick={closeSidebar}
                            aria-current={isActive ? "page" : undefined}
                            className={cn(
                              NAV_LINK_CLASS,
                              isActive
                                ? NAV_LINK_ACTIVE_CLASS
                                : sectionOpen
                                  ? "text-surface-900"
                                  : NAV_LINK_IDLE_CLASS,
                            )}
                          >
                            {isActive ? (
                              <motion.span
                                layoutId="nav-active-pill"
                                className={NAV_ACTIVE_PILL_CLASS}
                                transition={NAV_PILL_TRANSITION}
                              />
                            ) : null}
                            <span
                              className={cn(
                                "transition-colors",
                                isActive || sectionOpen
                                  ? "text-primary-600"
                                  : "text-surface-400 group-hover:text-surface-600",
                              )}
                            >
                              {item.icon}
                            </span>
                            <span className="min-w-0 flex-1 truncate">
                              {item.label}
                            </span>
                            {showSettingsBadge && (
                              <span className="flex h-[18px] w-[18px] items-center justify-center rounded-md bg-warning-fill text-[11px] font-bold text-white">
                                !
                              </span>
                            )}
                            {countBadge && (
                              <span
                                className="flex h-[18px] min-w-[18px] items-center justify-center rounded-md bg-error-fill px-1 text-[11px] font-semibold tabular-nums text-white"
                                aria-label={countBadge.label}
                              >
                                {countBadge.count > 99 ? "99+" : countBadge.count}
                              </span>
                            )}
                            {childItems.length > 0 ? (
                              <ChevronRight
                                className={cn(
                                  "h-3.5 w-3.5 transition-transform duration-200 ease-out",
                                  sectionOpen
                                    ? "rotate-90 text-surface-500"
                                    : "text-surface-400 group-hover:text-surface-600",
                                )}
                              />
                            ) : null}
                          </Link>
                          {childItems.length > 0 ? (
                            <div
                              className={cn(
                                "grid transition-[grid-template-rows] duration-200 ease-out",
                                sectionOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
                              )}
                              aria-hidden={!sectionOpen}
                              inert={!sectionOpen}
                            >
                              <div className="min-h-0 overflow-hidden">
                                <div className="relative ml-[18px] mt-0.5 space-y-0.5 border-l border-surface-200 pl-2.5">
                                  {childItems.map((child) => {
                                    const childActive = isNavPathActive(
                                      location.pathname,
                                      child.path,
                                      child.exactPath,
                                    );
                                    return (
                                      <Link
                                        key={child.id}
                                        to={child.path}
                                        onClick={closeSidebar}
                                        aria-current={childActive ? "page" : undefined}
                                        className={cn(
                                          NAV_LINK_CLASS,
                                          "relative h-7",
                                          childActive
                                            ? cn(
                                                NAV_LINK_ACTIVE_CLASS,
                                                "before:absolute before:-left-[11px] before:top-1.5 before:bottom-1.5 before:w-0.5 before:rounded-full before:bg-primary-500",
                                              )
                                            : NAV_LINK_IDLE_CLASS,
                                        )}
                                      >
                                        {childActive ? (
                                          <motion.span
                                            layoutId="nav-active-pill"
                                            className={NAV_ACTIVE_PILL_CLASS}
                                            transition={NAV_PILL_TRANSITION}
                                          />
                                        ) : null}
                                        <span className="min-w-0 flex-1 truncate">
                                          {child.label}
                                        </span>
                                      </Link>
                                    );
                                  })}
                                </div>
                              </div>
                            </div>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </nav>

            {/* Footer */}
            <div className="shrink-0 space-y-2 border-t border-surface-200 p-3 lg:border-t-0">
              <div
                className="flex items-center justify-between gap-2 px-1"
                data-skip-navigation-guard
              >
                <LanguageSwitcher variant="sidebar" />
                <SidebarPrivacyMenu onItemClick={closeSidebar} />
              </div>
              <div className="flex items-center gap-2.5 rounded-xl border border-surface-200 bg-surface-0 p-2 shadow-xs dark:bg-surface-100/60">
                <span
                  className={cn(
                    "flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-xs font-semibold shadow-glow",
                    brandFillClass("primary"),
                  )}
                >
                  {userInitials}
                </span>
                <div className="min-w-0 flex-1">
                  <TruncatedText className="block text-[13px] font-medium text-surface-900">
                    {user?.email ?? ""}
                  </TruncatedText>
                  {roleLabel ? (
                    <p className="text-[11px] text-surface-500">{roleLabel}</p>
                  ) : null}
                </div>
                <button
                  type="button"
                  onClick={handleLogoutClick}
                  title={tNav("logout")}
                  aria-label={tNav("logout")}
                  className="rounded-md p-1.5 text-surface-500 transition-colors hover:bg-error-50 hover:text-error-600"
                >
                  <LogOut className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </aside>

        {/* Main column: floating panel on desktop (inset shell) */}
        <div className="relative flex min-w-0 flex-1 flex-col bg-surface-50 lg:my-2 lg:mr-2 lg:min-h-[calc(100dvh-1rem)] lg:overflow-clip lg:rounded-2xl lg:shadow-panel">
          <div
            className="pointer-events-none absolute inset-x-0 top-0 h-80 bg-[radial-gradient(60%_100%_at_50%_0%,color-mix(in_oklab,var(--color-primary-500)_9%,transparent),transparent)] dark:bg-[radial-gradient(60%_100%_at_50%_0%,color-mix(in_oklab,var(--color-primary-500)_14%,transparent),transparent)]"
            aria-hidden
          />
          <header
            className={cn(
              "sticky z-30 flex h-14 shrink-0 items-center gap-2 border-b border-surface-200/70 bg-surface-50/75 px-3 backdrop-blur-xl backdrop-saturate-150 sm:px-6 lg:px-8",
              environmentBannerVisible
                ? ENVIRONMENT_BANNER_TOP_OFFSET_CLASS
                : "top-0",
            )}
          >
            <button
              type="button"
              onClick={() => setSidebarOpen(true)}
              aria-label={tNav("openMenu")}
              className="relative -ml-1 rounded-md p-2 text-surface-600 transition-colors hover:bg-surface-100 hover:text-surface-900 lg:hidden"
            >
              <Menu className="h-5 w-5" />
              {newPartnerBookingCount > 0 || impersonating ? (
                <span
                  className={cn(
                    "absolute right-1.5 top-1.5 h-2 w-2 rounded-full ring-2 ring-surface-50",
                    impersonating ? "bg-warning-500" : "bg-error-500",
                  )}
                  aria-label={
                    impersonating
                      ? tNav("impersonating")
                      : tNav("bookingsNew", { count: newPartnerBookingCount })
                  }
                />
              ) : null}
            </button>
            <Link to="/" className="block h-7 w-20 lg:hidden">
              <AppBrandLogo background="light" className="h-full w-full" />
            </Link>

            {breadcrumb.length > 0 ? (
              <nav
                aria-label={tNav("breadcrumb")}
                className="hidden min-w-0 items-center gap-1.5 text-sm lg:flex"
              >
                {breadcrumb.map((crumb, index) => {
                  const last = index === breadcrumb.length - 1;
                  return (
                    <span key={crumb.id} className="flex min-w-0 items-center gap-1.5">
                      {index > 0 ? (
                        <ChevronRight className="h-3.5 w-3.5 shrink-0 text-surface-300" aria-hidden />
                      ) : null}
                      {last || !crumb.path ? (
                        <span
                          className={cn(
                            "truncate",
                            last ? "font-medium text-surface-900" : "text-surface-500",
                          )}
                        >
                          {crumb.label}
                        </span>
                      ) : (
                        <Link
                          to={crumb.path}
                          className="truncate text-surface-500 transition-colors hover:text-surface-900"
                        >
                          {crumb.label}
                        </Link>
                      )}
                    </span>
                  );
                })}
              </nav>
            ) : null}

            <div className="ml-auto flex items-center gap-1.5">
              <button
                type="button"
                onClick={() => setQuickSearchOpen(true)}
                aria-label={tNav("search")}
                className={cn(
                  "flex h-8 items-center gap-2 rounded-lg border border-surface-200 bg-surface-0 px-2.5 text-[13px] text-surface-500 shadow-xs transition-colors",
                  "hover:border-surface-300 hover:text-surface-800 sm:w-56 dark:bg-surface-100/50",
                )}
              >
                <Search className="h-3.5 w-3.5 shrink-0" aria-hidden />
                <span className="hidden flex-1 text-left sm:inline">
                  {tNav("search")}
                </span>
                <kbd className="hidden rounded border border-surface-200 bg-surface-50 px-1 font-mono text-[10px] font-medium text-surface-500 sm:inline">
                  {quickSearchShortcutLabel()}
                </kbd>
              </button>
              <AppThemeToggle />
              {showAdminNotifications ? (
                <Link
                  to="/admin/notifications"
                  aria-label={tNav("adminNotificationsOpen", {
                    count: openNotificationCount,
                  })}
                  className={cn(TOPBAR_ICON_BUTTON_CLASS, "relative")}
                >
                  <Bell className="h-4 w-4" />
                  {openNotificationCount > 0 ? (
                    <span className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-error-500 ring-2 ring-surface-50" />
                  ) : null}
                </Link>
              ) : null}
            </div>
          </header>

          {/* Page content */}
          <main className="relative mx-auto flex w-full max-w-[1600px] flex-1 flex-col px-4 py-6 sm:px-6 lg:px-10 lg:py-9">
            <RouteEnterTransition pathname={location.pathname}>
              {children}
            </RouteEnterTransition>
          </main>
        </div>
      </div>

      <AlertModal
        closeLabel={tCommon("close")}
        isOpen={unsavedPromptOpen}
        onClose={closeUnsavedPrompt}
        title={tCommon("unsavedChanges.navigationTitle")}
        message={tCommon("unsavedChanges.navigationMessage")}
        variant="warning"
      />
      <Modal
        closeLabel={tCommon("close")}
        isOpen={showImpersonationLogoutModal}
        onClose={() => setShowImpersonationLogoutModal(false)}
        title={tNav("logoutWhileImpersonatingTitle", {
          name: user?.email || tCommon("user"),
        })}
        description={tNav("logoutWhileImpersonatingMessage")}
        size="sm"
        role="alertdialog"
        showCloseButton={!isStopping}
        footer={
          <ModalFooter>
            <Button
              variant="error"
              onClick={handleConfirmLogoutWhileImpersonating}
              disabled={isStopping}
            >
              {tNav("logout")}
            </Button>
            <Button
              variant="primary"
              onClick={handleStopImpersonatingFromLogoutModal}
              isLoading={isStopping}
            >
              {tNav("stopImpersonating")}
            </Button>
          </ModalFooter>
        }
      />

      <QuickSearchDialog
        destinations={quickSearchDestinations}
        isOpen={quickSearchOpen}
        onOpenChange={setQuickSearchOpen}
      />
    </div>
  );
}
